// Shared QR value normalization used by every scan entry point (phone camera lands
// directly on the public route so it doesn't need this; the in-app mobile scanner,
// the add-tool form scanner, and the add-tool-to-worksite scanner all funnel scanned
// text through this helper instead of comparing raw scanned strings directly).

// Identifies what kind of code was scanned so callers can branch without re-parsing.
// TOOL is URL-based today (".../tools/overview/<barcode>"), and so is LOGGER_OVERVIEW,
// the QR printed on v3 loggers (".../loggers/overview/<barcode>", the public logger
// overview behind a verification code). DEVICE is what the QR generator prints now
// (".../devices/<barcode>" for tools and loggers alike). LOGGER is the placeholder for everything not yet
// URL-based (currently: project/logger bare barcodes) - as more entities get their own QR
// URL format, give them their own BarcodeType value here instead of overloading LOGGER.
export enum BarcodeType {
  TOOL = 'TOOL',
  LOGGER = 'LOGGER',
  LOGGER_OVERVIEW = 'LOGGER_OVERVIEW',
  DEVICE = 'DEVICE',
}

export type ParsedQrValue = {
  /** The raw, unmodified text exactly as read from the QR code / scanner / manual input. */
  rawValue: string;
  /** The barcode: extracted from the URL when rawValue is a Kvalilog QR URL
   *  (".../devices/<barcode>", ".../tools/overview/<barcode>"...); otherwise identical to rawValue. */
  barcodeValue: string;
  /** What kind of code this was recognized as - see BarcodeType. */
  type: BarcodeType;
};

const TOOL_OVERVIEW_PATTERN = /\/tools\/overview\/([^/?#]+)/i;
const LOGGER_OVERVIEW_PATTERN = /\/loggers\/overview\/([^/?#]+)/i;
const DEVICE_PATTERN = /\/devices\/([^/?#]+)/i;

// A malformed escape in a foreign QR code must not break the scan.
function decodeSegment(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

export function parseQrValue(scanned: string): ParsedQrValue {
  const rawValue = (scanned || '').trim();

  const deviceMatch = rawValue.match(DEVICE_PATTERN);

  if (deviceMatch) {
    return { rawValue, barcodeValue: decodeSegment(deviceMatch[1]), type: BarcodeType.DEVICE };
  }

  const match = rawValue.match(TOOL_OVERVIEW_PATTERN);

  if (match) {
    return { rawValue, barcodeValue: decodeSegment(match[1]), type: BarcodeType.TOOL };
  }

  const loggerMatch = rawValue.match(LOGGER_OVERVIEW_PATTERN);

  if (loggerMatch) {
    return { rawValue, barcodeValue: decodeSegment(loggerMatch[1]), type: BarcodeType.LOGGER_OVERVIEW };
  }

  return { rawValue, barcodeValue: rawValue, type: BarcodeType.LOGGER };
}

export function buildDeviceQrUrl(barcode: string): string {
  return `${window.location.origin}/devices/${encodeURIComponent(barcode)}`;
}
